import React, { useState, useEffect } from 'react';
import api from '../../lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';

export default function ScheduleMaintenanceModal({ open, onClose, onSuccess }) {
  const [loading, setLoading] = useState(false);
  const [units, setUnits] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [formData, setFormData] = useState({
    unit_id: '',
    building_id: '',
    title: '',
    description: '',
    priority: 'medium',
    category: 'general',
    assigned_vendor_id: '',
    scheduled_date: '',
    estimated_cost: 0
  });

  useEffect(() => {
    if (open) {
      fetchUnits();
      fetchVendors();
    }
  }, [open]);

  const fetchUnits = async () => {
    try {
      const response = await api.get('/hoa/units');
      setUnits(response.data.units || []);
    } catch (error) {
      console.error('Error fetching units:', error);
      setUnits([]); // Set empty array to prevent errors
    }
  };

  const fetchVendors = async () => {
    try {
      const response = await api.get('/hoa/vendors');
      setVendors(response.data.vendors || []);
    } catch (error) {
      console.error('Error fetching vendors:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await api.post('/hoa/maintenance/schedule', formData);
      toast.success('Maintenance scheduled successfully!');
      onSuccess();
      onClose();
      setFormData({
        unit_id: '',
        building_id: '',
        title: '',
        description: '',
        priority: 'medium',
        category: 'general',
        assigned_vendor_id: '',
        scheduled_date: '',
        estimated_cost: 0
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to schedule maintenance');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Schedule Maintenance</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Unit</Label>
              <Select value={formData.unit_id} onValueChange={(val) => setFormData({...formData, unit_id: val})}>
                <SelectTrigger>
                  <SelectValue placeholder="Select unit" />
                </SelectTrigger>
                <SelectContent>
                  {units.map(unit => (
                    <SelectItem key={unit.id} value={unit.id}>
                      {unit.unit_number}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Priority</Label>
              <Select value={formData.priority} onValueChange={(val) => setFormData({...formData, priority: val})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="urgent">Urgent</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label>Title</Label>
            <Input 
              value={formData.title}
              onChange={(e) => setFormData({...formData, title: e.target.value})}
              placeholder="Brief description of issue"
              required
            />
          </div>

          <div className="space-y-2">
            <Label>Description</Label>
            <textarea 
              className="w-full border rounded p-2 min-h-[80px]"
              value={formData.description}
              onChange={(e) => setFormData({...formData, description: e.target.value})}
              placeholder="Detailed description"
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Category</Label>
              <Select value={formData.category} onValueChange={(val) => setFormData({...formData, category: val})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="general">General</SelectItem>
                  <SelectItem value="plumbing">Plumbing</SelectItem>
                  <SelectItem value="electrical">Electrical</SelectItem>
                  <SelectItem value="hvac">HVAC</SelectItem>
                  <SelectItem value="landscaping">Landscaping</SelectItem>
                  <SelectItem value="painting">Painting</SelectItem>
                  <SelectItem value="roofing">Roofing</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Assign Vendor (Optional)</Label>
              <Select value={formData.assigned_vendor_id} onValueChange={(val) => setFormData({...formData, assigned_vendor_id: val})}>
                <SelectTrigger>
                  <SelectValue placeholder="Select vendor" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {vendors.map(vendor => (
                    <SelectItem key={vendor.id} value={vendor.id}>
                      {vendor.company_name || vendor.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Scheduled Date</Label>
              <Input 
                type="date"
                value={formData.scheduled_date}
                onChange={(e) => setFormData({...formData, scheduled_date: e.target.value})}
              />
            </div>
            <div className="space-y-2">
              <Label>Estimated Cost ($)</Label>
              <Input 
                type="number"
                value={formData.estimated_cost}
                onChange={(e) => setFormData({...formData, estimated_cost: parseFloat(e.target.value) || 0})}
                min="0"
                step="0.01"
              />
            </div>
          </div>

          <div className="flex justify-end space-x-2 pt-4">
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={loading}>
              {loading ? 'Scheduling...' : 'Schedule Maintenance'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
